import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Alert,
  Chip,
  Stack,
  FormControlLabel,
  Switch,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/EditOutlined';
import ArchiveIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveIcon from '@mui/icons-material/UnarchiveOutlined';
import { useTranslation } from 'react-i18next';
import { colors } from '../../core/theme/theme';
import { CATALOG_SECTIONS, type CatalogDesignation, type CatalogSection } from '@altyor-kronos/shared-types';
import { useCollectionSnapshot } from '../../core/data/useCollectionSnapshot';
import { createDesignation, updateDesignation, setDesignationArchived } from '../../core/data/adminMutations';
import CatalogDesignationDialog, {
  type ExistingCatalogDesignation,
} from './CatalogDesignationDialog';
import { adminDataGridSx } from './adminDataGridSx';
import type { CatalogDesignationFormValues } from './validators';

export default function DesignationCatalogTab() {
  const { t } = useTranslation();
  const { rows, loading, error } = useCollectionSnapshot<CatalogDesignation>(
    'designationCatalog',
  );

  const [sectionFilter, setSectionFilter] = useState<CatalogSection>('rd');
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ExistingCatalogDesignation | null>(null);

  const allVariants = useMemo(
    () => [...new Set(rows.flatMap(({ data }) => data.variants ?? []))].sort(),
    [rows],
  );

  const gridRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows
      .filter(({ data }) => data.section === sectionFilter)
      .filter(({ data }) => showArchived || !data.isArchived)
      .filter(({ data }) => !q || data.name.toLowerCase().includes(q))
      .map(({ id, data }) => ({
        id,
        name: data.name,
        variants: data.variants ?? [],
        billingMode: data.billingMode ?? 'hourly',
        isArchived: data.isArchived,
        section: data.section,
      }));
  }, [rows, sectionFilter, showArchived, searchQuery]);

  const onCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const onEdit = (row: (typeof gridRows)[number]) => {
    setEditing({
      id: row.id,
      section: row.section,
      name: row.name,
      variants: row.variants,
      billingMode: row.billingMode,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (values: CatalogDesignationFormValues) => {
    if (editing) {
      await updateDesignation(editing.id, {
        name: values.name.trim(),
        billingMode: values.billingMode,
        variants: values.variants,
      });
    } else {
      await createDesignation({
        name: values.name.trim(),
        section: values.section,
        billingMode: values.billingMode,
        variants: values.variants,
      });
    }
  };

  const toggleArchive = async (id: string, currentlyArchived: boolean) => {
    await setDesignationArchived(id, !currentlyArchived);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('admin.designations.name'),
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'billingMode',
      headerName: t('admin.designations.billingMode'),
      width: 130,
      renderCell: (params) => (
        <Chip
          label={t(`projects.designations.billingMode.${params.value}`)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'variants',
      headerName: t('admin.designations.variants'),
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
          {(params.value as string[]).map((v: string) => (
            <Chip key={v} label={v} size="small" />
          ))}
        </Stack>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 110,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            aria-label={t('common.edit')}
            onClick={() => onEdit(params.row)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={
              params.row.isArchived
                ? t('admin.designations.restore')
                : t('admin.designations.archive')
            }
            onClick={() => toggleArchive(params.row.id, params.row.isArchived)}
          >
            {params.row.isArchived ? (
              <UnarchiveIcon fontSize="small" />
            ) : (
              <ArchiveIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, rowGap: 1 }} useFlexGap>
          {CATALOG_SECTIONS.map((s) => (
            <Chip
              key={s}
              label={t(`projects.designations.sections.${s}`)}
              onClick={() => setSectionFilter(s)}
              variant={sectionFilter === s ? 'filled' : 'outlined'}
              sx={sectionFilter === s ? { bgcolor: colors.yellow, color: '#000' } : undefined}
            />
          ))}
        </Stack>
        <Button variant="contained" color="primary" onClick={onCreate} sx={{ flexShrink: 0 }}>
          {t('admin.designations.new')}
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
          mb: 1,
        }}
      >
        <TextField
          size="small"
          placeholder={t('admin.designations.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ opacity: 0.5 }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: { xs: '100%', sm: 260 }, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showArchived}
              onChange={(_, checked) => setShowArchived(checked)}
              size="small"
            />
          }
          label={t('admin.designations.showArchived')}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}
      <Box sx={{ height: 'calc(100vh - 320px)', minHeight: 400 }}>
        <DataGrid
          rows={gridRows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnSelector
          density="standard"
          sx={{
            ...adminDataGridSx,
            '& .archived-row': { opacity: 0.5 },
          }}
          getRowClassName={(params) => (params.row.isArchived ? 'archived-row' : '')}
          hideFooter
        />
      </Box>

      <CatalogDesignationDialog
        open={dialogOpen}
        editing={editing}
        onSubmit={handleSubmit}
        onClose={() => setDialogOpen(false)}
        availableVariants={allVariants}
      />
    </Box>
  );
}
